import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    creatorbase,
    creatorbaseQueries,
    getPaginatedIdQueryParam,
    instagramQueries,
    InstagramUser,
    InstagramUserImage,
    InstagramUserStatsCache,
} from '@round/api';
import { useInfinitePosts, usePostsTotals } from '@round/creatorbase-helpers/api-hooks';
import {
    useInstagramUserImageListQueries,
    useInstagramUserListQueries,
    useInstagramUserStatsCacheListQueries,
} from '@round/platforms/instagram';
import { isNumber } from 'utility/utility';
import uniq from 'lodash/uniq';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';

export type InstagramReportPostsTableRow = creatorbase.InstagramPost & {
    user: InstagramUser | null;
    userStatsCache: InstagramUserStatsCache | null;
    image: InstagramUserImage | null;
};

// Serves as both the posts page size and the account-id batch size, so one posts page never needs
// more than one batch of account requests.
const BATCH_SIZE = 50;

// One request per batch of account ids. The declarative reads and the imperative ensureQueryData
// calls build their params here, so equal id sets produce identical query keys and share cache
// entries.
const buildUserParams = (userIds: number[]) => ({ id: userIds.join(','), page_size: userIds.length });

// The user-image list takes its ids positionally, so page_size never reaches the key here.
const buildUserImageParams = (userIds: number[]) => ({ ids: userIds });

const buildUserStatsCacheParams = (userIds: number[]) => ({ user_id: userIds, page_size: userIds.length });

// Every page issues a query — disabled when the page has no accounts — so a query's index is its
// page index.
const collectLoadingPageIndexes = (results: { isLoading: boolean }[]) =>
    new Set(results.flatMap((result, index) => (result.isLoading ? [index] : [])));

const getInstagramUserIds = (posts: creatorbase.InstagramPost[]) =>
    uniq(posts.map((post) => post.instagram_details?.account_id).filter(isNumber));

const assembleRows = (
    posts: creatorbase.InstagramPost[],
    usersById: Map<number, InstagramUser>,
    imagesByUserId: Map<number, InstagramUserImage>,
    statsByUserId: Map<number, InstagramUserStatsCache>
): InstagramReportPostsTableRow[] =>
    posts.map((post) => {
        const accountId = post.instagram_details?.account_id;
        return {
            ...post,
            user: isNumber(accountId) ? usersById.get(accountId) ?? null : null,
            userStatsCache: isNumber(accountId) ? statsByUserId.get(accountId) ?? null : null,
            image: isNumber(accountId) ? imagesByUserId.get(accountId) ?? null : null,
        };
    });

export const getReportInstagramPostsParams = (campaignId: number): creatorbase.GetPostsParams => ({
    campaign_id: campaignId.toString(),
    ordering: '-view_count',
    exclude_statuses: backupPostsStatuses.join(','),
});

// Totals must cover exactly the list's filtered set, so they're derived from the list params
// rather than built independently.
const getReportInstagramPostsTotalsParams = (campaignId: number) => {
    const { ordering, ...params } = getReportInstagramPostsParams(campaignId);
    return params;
};

export function useInstagramReportCampaignPosts(campaignId: number) {
    const infinitePostsQuery = useInfinitePosts({
        ...getReportInstagramPostsParams(campaignId),
        page_size: BATCH_SIZE,
    });

    // Rows are partial until every page is loaded, so the footers always read API totals rather than
    // reducing over the rendered rows. isLoading, not isFetching: a background refetch would
    // otherwise skeleton every footer while the row cells keep their values.
    const { data: postsTotals, isLoading: areTotalsLoading } = usePostsTotals(
        getReportInstagramPostsTotalsParams(campaignId)
    );

    const postPages = (infinitePostsQuery.data?.pages ?? []).map((page) => page.results as creatorbase.InstagramPost[]);
    const posts = postPages.flat();

    // Account ids are batched by posts page, so loading another page never changes an earlier page's
    // query key.
    const accountIdBatches = postPages.map(getInstagramUserIds);

    const usersData = useInstagramUserListQueries(
        accountIdBatches.map((ids) => ({
            params: buildUserParams(ids),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data?.results ?? []).map((user) => [user.id, user])),
                loadingPages: collectLoadingPageIndexes(results),
            }),
        }
    );

    const userImagesData = useInstagramUserImageListQueries(
        accountIdBatches.map((ids) => ({
            params: buildUserImageParams(ids),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data ?? []).map((image) => [image.user_id, image])),
                loadingPages: collectLoadingPageIndexes(results),
            }),
        }
    );

    const userStatsCacheData = useInstagramUserStatsCacheListQueries(
        accountIdBatches.map((ids) => ({
            params: buildUserStatsCacheParams(ids),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((stats) => [stats.user_id, stats])
                ),
                loadingPages: collectLoadingPageIndexes(results),
            }),
        }
    );

    const pageIndexByPostId = new Map(
        postPages.flatMap((pagePosts, pageIndex) => pagePosts.map((post) => [post.id, pageIndex] as const))
    );

    // A row's loading state is its own page's, so a later page's fetch never skeletons earlier rows.
    const getIsAccountDataLoading = (post: InstagramReportPostsTableRow) => {
        if (!isNumber(post.instagram_details?.account_id)) {
            return false;
        }

        const pageIndex = pageIndexByPostId.get(post.id);
        if (pageIndex === undefined) {
            return false;
        }

        return (
            usersData.loadingPages.has(pageIndex) ||
            userImagesData.loadingPages.has(pageIndex) ||
            userStatsCacheData.loadingPages.has(pageIndex)
        );
    };

    return {
        data: assembleRows(posts, usersData.byId, userImagesData.byId, userStatsCacheData.byId),
        getIsAccountDataLoading,
        hasError: infinitePostsQuery.isError,
        isLoading: infinitePostsQuery.isLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage: infinitePostsQuery.hasNextPage,
        isFetchingNextPage: infinitePostsQuery.isFetchingNextPage,
        fetchNextPage: infinitePostsQuery.fetchNextPage,
    };
}

// Imperative twin: read-or-fetch enriched posts from the cache.
// Use from event handlers (e.g. copy-to-clipboard) where a live subscription
// isn't wanted. Shares keys with the hook, so an already-rendered table is a
// pure cache hit.
export function useEnsureInstagramReportCampaignPosts() {
    const queryClient = useQueryClient();

    return useCallback(
        async (campaignId: number): Promise<InstagramReportPostsTableRow[]> => {
            const posts = (await queryClient.ensureQueryData(
                creatorbaseQueries.posts.options.listAll(getReportInstagramPostsParams(campaignId))
            )) as creatorbase.InstagramPost[];

            const idBatches = getPaginatedIdQueryParam(getInstagramUserIds(posts), BATCH_SIZE);

            const [users, images, stats] = await Promise.all([
                Promise.all(
                    idBatches.map((ids) =>
                        queryClient.ensureQueryData(instagramQueries.user.options.list(buildUserParams(ids)))
                    )
                ),
                Promise.all(
                    idBatches.map((ids) => queryClient.ensureQueryData(instagramQueries.userImage.options.list(ids)))
                ),
                Promise.all(
                    idBatches.map((ids) =>
                        queryClient.ensureQueryData(
                            instagramQueries.userStatsCache.options.list(buildUserStatsCacheParams(ids))
                        )
                    )
                ),
            ]);

            const usersById = new Map(users.flatMap((result) => result.results).map((user) => [user.id, user]));
            const imagesByUserId = new Map(images.flat().map((image) => [image.user_id, image]));
            const statsByUserId = new Map(
                stats.flatMap((result) => result.results).map((stat) => [stat.user_id, stat])
            );

            return assembleRows(posts, usersById, imagesByUserId, statsByUserId);
        },
        [queryClient]
    );
}
